// Phase 2 — read-only filing tracker shown post-PAID on the case page.
// Swaps in place of CaseFilePanel when case_meta.status is PAID_HANDED_OFF /
// IN_FILING / INCORPORATED. Loads from GET /api/cases/:id/filing-progress.
import { useEffect, useState } from "react";
import { CheckCircle2, AlertTriangle, Loader2 } from "lucide-react";
import * as api from "../api";

function fmtDate(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString("en-IN", { day: "numeric", month: "short" });
  } catch {
    return "";
  }
}

export default function FilingTrackerPanel({ caseData }) {
  const caseId = caseData?.case_meta?.case_id;
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!caseId) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const d = await api.getFilingProgress(caseId);
        if (!cancelled) setData(d);
      } catch {
        if (!cancelled) setData(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [caseId]);

  const status = caseData?.case_meta?.status;

  return (
    <div className="h-full overflow-y-auto chat-scroll" data-testid="filing-tracker-panel">
      <div className="p-6 sm:p-8">
        {/* Header */}
        <div className="pb-6 border-b border-slate-200 mb-6">
          <div className="text-[11px] uppercase tracking-[0.12em] font-semibold text-slate-500">
            Case file
          </div>
          <div className="flex items-baseline justify-between mt-2">
            <h2 className="font-display text-2xl text-slate-900 font-semibold" data-testid="case-id-header">
              {caseId}
            </h2>
            <span className="text-xs px-2 py-0.5 rounded-full bg-[#C5283F]/10 text-[#7A0E1F] border border-[#C5283F]/30">
              {status || "DRAFT"}
            </span>
          </div>
        </div>

        <div className="text-[11px] uppercase tracking-[0.12em] font-semibold text-slate-500 mb-4">
          Filing progress
        </div>

        {loading && (
          <div className="flex items-center gap-2 text-sm text-slate-500" data-testid="filing-tracker-loading">
            <Loader2 size={14} className="animate-spin" /> Loading…
          </div>
        )}

        {!loading && data && !data.started && (
          <div className="rounded-lg border border-slate-200 bg-slate-50 p-4 text-sm text-slate-600"
               data-testid="filing-tracker-empty">
            Your filing tracker will begin the moment our team picks up your file.
          </div>
        )}

        {!loading && data && data.started && (
          <div className="relative pl-3" data-testid="filing-tracker-list">
            <div className="absolute left-[11px] top-2 bottom-2 w-px bg-slate-200" />
            {data.pending_founder_response && (
              <div className="mb-4 rounded-lg border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-900"
                   data-testid="response-sent-banner">
                Your response has been sent — our team will review and update the tracker.
              </div>
            )}
            {data.stages.map((s) => (
              <StageRow key={s.key} stage={s} actionMessage={data.action_message} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function StageRow({ stage, actionMessage }) {
  const isDone = stage.state === "done";
  const isCurrent = stage.is_current && !isDone;
  const isAction = stage.needs_action;
  const isPending = stage.state === "pending" && !isCurrent;

  const dot = (
    <div
      className={[
        "w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-semibold z-10 ring-4 ring-[#FFF8F1]",
        isAction
          ? "bg-amber-500 text-white"
          : isDone
          ? "bg-[#C5283F] text-white"
          : isCurrent
          ? "bg-[#C5283F] text-white"
          : "bg-white border border-slate-200 text-slate-400",
      ].join(" ")}
    >
      {isDone ? "✓" : isAction ? "!" : ""}
    </div>
  );

  if (isAction) {
    return (
      <div className="flex items-start gap-3 mb-3 relative" data-testid={`stage-${stage.key}`}>
        {dot}
        <div
          className="flex-1 rounded-lg border border-amber-300 bg-amber-50 p-3"
          data-testid={`stage-${stage.key}-action-card`}
        >
          <div className="flex items-center gap-2 text-amber-900 font-semibold text-sm">
            <AlertTriangle size={14} /> {stage.label}
          </div>
          <div className="text-sm text-amber-900 mt-1 leading-relaxed">
            {stage.copy}
          </div>
          {actionMessage && (
            <div className="text-sm text-amber-950 mt-2 leading-relaxed border-t border-amber-200 pt-2"
                 data-testid={`stage-${stage.key}-action-message`}>
              {actionMessage}
            </div>
          )}
          <a href="#chat"
             onClick={(e) => {
               e.preventDefault();
               const ev = new CustomEvent("infin:respond", { detail: { stage: stage.key } });
               window.dispatchEvent(ev);
             }}
             className="mt-3 inline-block text-xs font-semibold text-amber-900 underline hover:text-amber-950"
             data-testid={`stage-${stage.key}-respond-btn`}>
            Respond via Infi →
          </a>
        </div>
      </div>
    );
  }

  return (
    <div className="flex items-center gap-3 mb-3 relative" data-testid={`stage-${stage.key}`}>
      {dot}
      <div className="flex-1 min-w-0">
        <div
          className={[
            "text-sm flex items-center gap-2",
            isCurrent ? "font-semibold text-slate-900" : "",
            isDone ? "text-slate-600" : "",
            isPending ? "text-slate-400" : "",
          ].join(" ")}
        >
          <span>{stage.label}</span>
          {isDone && stage.timestamp && (
            <span className="text-xs text-slate-400 font-normal" data-testid={`stage-${stage.key}-date`}>
              · {fmtDate(stage.timestamp)}
            </span>
          )}
          {isDone && <CheckCircle2 size={14} className="text-emerald-600 ml-0.5" />}
        </div>
        {isCurrent && stage.copy && (
          <div className="text-xs text-slate-600 mt-1 leading-relaxed" data-testid={`stage-${stage.key}-copy`}>
            {stage.copy}
          </div>
        )}
      </div>
    </div>
  );
}
